export const cleanPhoneNumber = (value: string): string => {
  if (!value) return '';

  const hasLeadingPlus = value.charAt(0) === '+';
  const digitsOnly = value.replace(/\D/g, '');

  return hasLeadingPlus ? `+${digitsOnly}` : digitsOnly;
};

export const createPhoneChangeHandler = (
  originalOnChange: (value: string) => void,
) => {
  return (event: React.ChangeEvent<HTMLInputElement>) => {
    const cleanedValue = cleanPhoneNumber(event.target.value);
    originalOnChange(cleanedValue);
  };
};

export function formatUtc(dateIso?: string | null) {
  if (!dateIso) return '';
  const d = new Date(dateIso);
  const date = d.toLocaleDateString(undefined, {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
  const time = d
    .toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replace(':', ':');
  return `${date} - ${time} UTC`;
}

export const formatDateOnly = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : '00/00/0000';

export interface UserDatePreferences {
  dateFormat?: string | null;
  is24HourFormat?: boolean | null;
  zoneInfo?: string | null;
}

export function formatUserDate(
  dateIso?: string | null,
  prefs?: UserDatePreferences,
): string {
  if (!dateIso) return '';
  const date = new Date(dateIso);
  if (isNaN(date.getTime())) return '';

  const timeZone = prefs?.zoneInfo || 'GMT';
  const dateFormatStr = prefs?.dateFormat || 'MM/dd/yyyy';

  const dateParts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(date);

  const day = dateParts.find(p => p.type === 'day')?.value ?? '00';
  const month = dateParts.find(p => p.type === 'month')?.value ?? '00';
  const year = dateParts.find(p => p.type === 'year')?.value ?? '0000';

  return dateFormatStr.replace('MM', month).replace('dd', day).replace('yyyy', year);
}

export function formatUserTime(
  dateIso?: string | null,
  prefs?: UserDatePreferences,
): string {
  if (!dateIso) return '';
  const date = new Date(dateIso);
  if (isNaN(date.getTime())) return '';

  const timeZone = prefs?.zoneInfo || 'GMT';
  const is24Hour = prefs?.is24HourFormat ?? false;

  const timeParts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: !is24Hour,
  }).formatToParts(date);

  const hour = timeParts.find(p => p.type === 'hour')?.value ?? '00';
  const minute = timeParts.find(p => p.type === 'minute')?.value ?? '00';
  const dayPeriod = timeParts.find(p => p.type === 'dayPeriod')?.value;

  return dayPeriod ? `${hour}:${minute} ${dayPeriod}` : `${hour}:${minute}`;
}

export function formatUserDateTime(
  dateIso?: string | null,
  prefs?: UserDatePreferences,
): string {
  if (!dateIso) return '';
  const date = new Date(dateIso);
  if (isNaN(date.getTime())) return '';

  const timeZone = prefs?.zoneInfo || 'GMT';
  const dateFormatStr = prefs?.dateFormat || 'MM/dd/yyyy';
  const is24Hour = prefs?.is24HourFormat ?? false;

  const dateParts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(date);

  const day = dateParts.find(p => p.type === 'day')?.value ?? '00';
  const month = dateParts.find(p => p.type === 'month')?.value ?? '00';
  const year = dateParts.find(p => p.type === 'year')?.value ?? '0000';

  const formattedDate = dateFormatStr
    .replace('MM', month)
    .replace('dd', day)
    .replace('yyyy', year);

  const timeParts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: !is24Hour,
  }).formatToParts(date);

  const hour = timeParts.find(p => p.type === 'hour')?.value ?? '00';
  const minute = timeParts.find(p => p.type === 'minute')?.value ?? '00';
  const dayPeriod = timeParts.find(p => p.type === 'dayPeriod')?.value;

  const formattedTime = dayPeriod
    ? `${hour}:${minute} ${dayPeriod}`
    : `${hour}:${minute}`;

  return `${formattedDate} - ${formattedTime}`;
}

export function normalizeText(value: string): string {
  return value.trim().toLowerCase();
}

export function toNonEmptyText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;

  const trimmedValue = value.trim();
  if (!trimmedValue) return undefined;
  return trimmedValue;
}
